import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useTheme } from '../../contexts/ThemeContext';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import type { LoginData } from '../../types/user';
import { Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';
import { AuthService } from '../../services/authService';
import { markNewSignupFlow } from '../../utils/googleAuth';

const Login: React.FC = () => {
  console.log('🔍 Login: Component rendered');
  
  const [formData, setFormData] = useState<LoginData>({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login, sendPasswordReset, user, isLoggedIn, setUser } = useAuth();
  const { language, setLanguage, isRTL } = useLocalization();
  const { isDark, toggleTheme } = useTheme();
  const errorRef = useRef<string | null>(null);
  
  console.log('🔍 Login: Current state:', {
    isLoggedIn,
    user: user ? { id: user.id, email: user.email, shouldShowTrialModal: user.shouldShowTrialModal } : null
  });
  
  // Clear any stale errors from localStorage on component mount
  // Errors should only be shown during active login attempts, not on page load/refresh
  useEffect(() => {
    // Clear any stored error from previous sessions
    localStorage.removeItem('loginError');
    setError(null);
    errorRef.current = null;
  }, []);

  // Handle login error events (only during active login attempts)
  useEffect(() => {
    const handleLoginError = (event: CustomEvent) => {
      setError(event.detail);
      errorRef.current = event.detail;
    };
    
    window.addEventListener('loginError', handleLoginError as EventListener);
    
    return () => {
      window.removeEventListener('loginError', handleLoginError as EventListener);
    };
  }, []);
  

  // Navigate to loading page when user logs in
  useEffect(() => {
    if (isLoggedIn && user) {
      console.log('🔍 Login: User logged in, navigating to loading page');
      // Clear any error state and localStorage on successful login
      setError(null);
      errorRef.current = null;
      localStorage.removeItem('loginError');
      navigate('/loading', { replace: true });
    }
  }, [isLoggedIn, user, navigate]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    errorRef.current = null;
    localStorage.removeItem('loginError'); // Clear any stored error

    try {
      await login(formData);
      setSuccess(isRTL ? 'تم تسجيل الدخول بنجاح!' : 'Login successful!');
    } catch (err: any) {
      
      const errorMessage = err.message || err.toString() || (isRTL ? 'حدث خطأ أثناء تسجيل الدخول' : 'Login failed');
      
      // Store error in localStorage to persist across component remounts
      localStorage.setItem('loginError', errorMessage);
      
      // Set both ref and state
      errorRef.current = errorMessage;
      setError(errorMessage);
      
      // Dispatch custom event to force error display
      window.dispatchEvent(new CustomEvent('loginError', { detail: errorMessage }));
      
      // Force a re-render to ensure error is displayed
      setTimeout(() => {
        if (errorRef.current) {
          setError(errorRef.current);
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const outcome = await AuthService.signInWithGoogle(true);
      if (outcome.type === 'needs_registration_consent') {
        navigate('/auth/google-create-account');
        return;
      }
      if (outcome.type === 'cancelled') {
        return;
      }
      setUser(outcome.user);
      markNewSignupFlow(false);
      setSuccess(isRTL ? 'تم تسجيل الدخول بنجاح!' : 'Login successful!');
      navigate('/loading', { replace: true });
    } catch (err: any) {
      const errorMessage =
        err.message || (isRTL ? 'فشل تسجيل الدخول عبر Google' : 'Google Sign-In failed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setError(isRTL ? 'يرجى إدخال البريد الإلكتروني' : 'Please enter your email');
      return;
    }

    try {
      await sendPasswordReset(resetEmail);
      setSuccess(isRTL ? 'تم إرسال رابط إعادة تعيين كلمة المرور' : 'Password reset link sent');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (err: any) {
      setError(err.message || (isRTL ? 'فشل في إرسال رابط إعادة التعيين' : 'Failed to send reset link'));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error || errorRef.current) {
      setError(null);
      errorRef.current = null;
      localStorage.removeItem('loginError');
    }
  };

  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      
      {/* Header */}
      <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <MorafaaLogo size="sm" variant="light" />
              <span className="text-xl font-bold text-white">
                {isRTL ? 'مرافعة' : 'Morafaa'}
              </span>
            </div>
            
            {/* Home Button, Theme & Language Toggle */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <Link
                to="/"
                className="px-3 py-1 text-sm font-medium text-white/80 hover:text-white transition-colors duration-200"
              >
                {isRTL ? 'الرئيسية' : 'Home'}
              </Link>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
                title={language === 'ar' ? 'تبديل المظهر' : 'Toggle Theme'}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              
              <button
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className="px-3 py-1 text-sm rounded-md transition-colors bg-white/20 text-white hover:bg-white/30"
              >
                {language === 'ar' ? 'English' : 'العربية'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 min-h-screen">
      <div className="w-full max-w-md">

        {/* Login Form */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="m3-headline-large text-slate-800 dark:text-white mb-2 tracking-tight">
              {isRTL ? 'مرحباً بك في مرافعة' : 'Welcome to Morafaa'}
            </h1>
            <p className="m3-body-large text-slate-600 dark:text-slate-300">
              {isRTL ? 'سجل دخولك للوصول إلى حسابك' : 'Sign in to access your account'}
            </p>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm font-medium">
                {error}
              </p>
            </div>
          )}

                  {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-green-600 dark:text-green-400 text-sm font-medium flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                        {success}
              </p>
                    </div>
                  )}

          {/* Login Form */}
          {!showForgotPassword ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block m3-label-large text-slate-700 dark:text-slate-300 mb-2">
                  {isRTL ? 'البريد الإلكتروني' : 'Email'}
                  </label>
                  <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200 m3-body-large text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={isRTL ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                  />
                </div>
                </div>

              <div>
                <label htmlFor="password" className="block m3-label-large text-slate-700 dark:text-slate-300 mb-2">
                  {isRTL ? 'كلمة المرور' : 'Password'}
                  </label>
                  <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className={`w-full py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200 m3-body-large text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 ${
                      isRTL ? 'pl-12 pr-4' : 'pr-12 pl-4'
                    }`}
                    placeholder={isRTL ? 'أدخل كلمة المرور' : 'Enter your password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-200 ${
                      isRTL ? 'right-3' : 'right-3'
                    }`}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                    </button>
                  </div>
                </div>

              <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                  className="m3-label-medium text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-primary transition-colors duration-200 transition-colors duration-200"
                  >
                  {isRTL ? 'نسيت كلمة المرور؟' : 'Forgot password?'}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                className="w-full bg-primary hover:bg-primary-dark disabled:bg-slate-400 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    <span className="m3-label-large">
                      {isRTL ? 'جاري تسجيل الدخول...' : 'Signing in...'}
                    </span>
                    </div>
                  ) : (
                  <span className="m3-label-large">
                    {isRTL ? 'تسجيل الدخول' : 'Sign In'}
                  </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full mt-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white font-medium py-3 px-4 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.5 5.5-6.4 7l.1.1 6.3 5.3C39.1 37.1 44 31.5 44 24c0-1.2-.1-2.3-.4-3.5z"/>
                  </svg>
                  {isRTL ? 'المتابعة باستخدام Google' : 'Continue with Google'}
                </button>
              </form>
              ) : (
            /* Forgot Password Form */
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div className="text-center mb-6">
                    <h3 className="m3-headline-small text-slate-800 dark:text-white mb-2">
                  {isRTL ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
                    </h3>
                    <p className="m3-body-medium text-slate-600 dark:text-slate-300">
                  {isRTL ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين' : 'Enter your email and we\'ll send you a reset link'}
                    </p>
                  </div>

              <div>
                <label htmlFor="resetEmail" className="block m3-label-large text-slate-700 dark:text-slate-300 mb-2">
                  {isRTL ? 'البريد الإلكتروني' : 'Email'}
                    </label>
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200 m3-body-large text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400"
                  placeholder={isRTL ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                />
                  </div>

              <div className="flex space-x-3 rtl:space-x-reverse">
                    <button
                      type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 py-3 px-4 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 m3-label-large"
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button
                      type="submit"
                  className="flex-1 bg-primary hover:bg-primary-dark text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl m3-label-large"
                >
                  {isRTL ? 'إرسال' : 'Send'}
                    </button>
                  </div>
                </form>
              )}

          {/* Sign Up Link */}
          <div className="mt-8 text-center">
            <p className="m3-body-medium text-slate-600 dark:text-slate-300">
              {isRTL ? 'ليس لديك حساب؟' : "Don't have an account?"}{' '}
                <Link
                  to="/signup"
                className="font-medium text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-primary transition-colors duration-200"
              >
                {isRTL ? 'سجل الآن' : 'Sign up'}
                </Link>
            </p>
              </div>

        </div>
      </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Login; 